import React, { useEffect, useState } from "react";
import { api, BACKEND_URL, euro } from "@/lib/api";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PurchaseReceipt } from "@/components/PurchaseReceipt";
import { RefreshCw, FileText, ShoppingCart, Download, Image as ImageIcon, Clock } from "lucide-react";
import { useOperator } from "@/components/mobile/operators";
import { OperatorMark } from "@/components/mobile/OperatorLogo";
import { WinetData, SimFacts } from "@/components/mobile/WinetRequestBox";
import { UnoMobileDealerBox } from "@/components/mobile/UnoMobileBox";

export const STATUS = {
  nuova: { label: "Nuova", cls: "bg-blue-500 hover:bg-blue-500" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-amber-500 hover:bg-amber-500 text-black" },
  completata: { label: "Completata", cls: "bg-teal-600 hover:bg-teal-600" },
  annullata: { label: "Annullata", cls: "bg-slate-500 hover:bg-slate-500" },
  errore: { label: "Errore", cls: "bg-red-600 hover:bg-red-600" },
};
export const TYPE = { attivazione: "Attiva SIM", ricarica: "Ricarica", info: "Info SIM", cambio_promo: "Cambio promo", sostituzione_sim: "Sostituzione SIM", cambio_intestatario: "Cambio intestatario" };
const fmt = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s || "—"; } };

export default function DealerItaliaMobile() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [authFilter, setAuthFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const op = useOperator();

  const load = () => {
    setLoading(true);
    api.get("/italiamobile/requests", { params: { operator: op.key } }).then((r) => setRows(r.data || [])).catch(() => toast.error("Errore caricamento")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = (r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    const authed = !!(r.authorization || {}).authorized;
    if (authFilter === "authorized" && !authed) return false;
    if (authFilter === "pending" && (authed || r.request_type !== "attivazione")) return false;
    return true;
  };
  const visible = rows.filter(matches);

  const payLabel = (r) => {
    const p = r.payment || {};
    if (p.method === "uno_account") return "Account Uno Mobile del dealer";
    if (r.request_type === "cambio_promo" || (p.service_charged && ["sostituzione_sim", "cambio_intestatario"].includes(r.request_type))) return `Plafond ${euro(p.total)}${p.refunded ? " · rimborsato" : ""}`;
    if (r.request_type === "ricarica" && p.topup_charged) return `Plafond ${euro(p.total)}${p.refunded ? " · rimborsato" : ""}`;
    if (r.request_type !== "attivazione") return "—";
    if (p.method === "customer") return p.checkout_url ? "Cliente (Stripe) · link inviato" : "Cliente (Stripe)";
    return `Plafond${p.paid ? " · pagato" : ""}`;
  };
  const attUrl = (r, kind) => `${BACKEND_URL}/api/italiamobile/requests/${r.id}/attachment/${kind}`;
  const pdaUrl = (r) => `${BACKEND_URL}/api/italiamobile/requests/${r.id}/pda`;

  return (
    <div className="space-y-6" data-testid="dealer-im-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><ShoppingCart className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="font-head text-2xl font-semibold" data-testid="dealer-im-title"><span className="inline-flex items-center gap-3"><OperatorMark op={op} size="lg" testid="dealer-im-logo" />Console SIM {op.name}</span></h1>
          <p className="text-sm text-muted-foreground">Stato, autorizzazione cliente e pagamento delle tue pratiche {op.name}.</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={load} data-testid="dealer-im-refresh"><RefreshCw className="w-4 h-4" /> Aggiorna</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-52" data-testid="dealer-im-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Stato: tutti</SelectItem>
            <SelectItem value="nuova">Nuova</SelectItem>
            <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
            <SelectItem value="completata">Completata</SelectItem>
            <SelectItem value="annullata">Annullata</SelectItem>
          </SelectContent>
        </Select>
        <Select value={authFilter} onValueChange={setAuthFilter}>
          <SelectTrigger className="h-9 w-56" data-testid="dealer-im-auth-filter"><SelectValue placeholder="Autorizzazione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Autorizzazione: tutte</SelectItem>
            <SelectItem value="pending">Da autorizzare</SelectItem>
            <SelectItem value="authorized">Autorizzate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-4 glow-border">
        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Caricamento…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="dealer-im-empty">Nessuna pratica trovata.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Pratica</TableHead><TableHead>Tipo</TableHead><TableHead>Cliente</TableHead>
              <TableHead>Piano</TableHead><TableHead>Stato</TableHead><TableHead>Autorizzazione</TableHead>
              <TableHead>Pagamento</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visible.map((r) => {
                const st = STATUS[r.status] || { label: r.status, cls: "" };
                const auth = r.authorization || {};
                return (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setSel(r)} data-testid={`dealer-im-row-${r.id}`}>
                    <TableCell className="font-mono text-xs"><span className="flex items-center gap-2"><OperatorMark op={op} testid={`dealer-im-row-logo-${r.id}`} />#{r.id.slice(-6)}</span></TableCell>
                    <TableCell className="text-sm">{TYPE[r.request_type] || r.request_type}{r.is_migration && <Badge variant="outline" className="ml-1 text-[10px]">MNP</Badge>}</TableCell>
                    <TableCell className="text-sm">{[r.nome, r.cognome].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell className="text-sm">{r.piano || "—"}</TableCell>
                    <TableCell><Badge className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell className="text-sm">
                      {auth.authorized
                        ? <Badge variant="outline" className="text-teal-600 border-teal-500/40">{auth.method === "sms" ? "SMS" : "PDA firmato"}</Badge>
                        : (r.request_type === "attivazione" ? <span className="text-amber-600 text-xs">In attesa</span> : <span className="text-muted-foreground">—</span>)}
                    </TableCell>
                    <TableCell className="text-sm">{payLabel(r)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-3xl" data-testid="dealer-im-detail">
          {sel && (
            <>
              <DialogHeader><DialogTitle data-testid="dealer-im-detail-title" className="flex items-center gap-2"><OperatorMark op={op} />{op.name} · Pratica #{sel.id.slice(-6)} · {TYPE[sel.request_type] || sel.request_type}</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-2 gap-3 items-start"><div className="space-y-3">
                <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
                  {[["Cliente", [sel.nome, sel.cognome].filter(Boolean).join(" ")], ["Codice fiscale", sel.codice_fiscale], ["Email", sel.email], ["Telefono", sel.telefono || sel.phone_number], ["Piano", sel.piano], ["ICCID", sel.iccid], ["Numero assegnato", sel.assigned_number], ...(sel.request_type === "attivazione" ? [["Attivazione", sel.is_migration ? "Portabilità (MNP)" : "Nuovo numero"]] : []), ...SimFacts(sel)].filter(([, v]) => v).map(([k, v]) => (
                    <React.Fragment key={k}><span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-words">{k === "Telefono" ? <PhoneDisplay value={v} data-testid="dealer-im-detail-phone" /> : v}</span></React.Fragment>
                  ))}
                </div>
                <WinetData data={(sel.winet || {}).response} testid="dealer-im-operator-data" />
                <UnoMobileDealerBox request={sel} onChanged={(r) => { setSel(r); setRows((rs) => rs.map((x) => (x.id === r.id ? r : x))); }} />

                {["attivazione", "cambio_promo"].includes(sel.request_type) && (
                  <div className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="detail-payment">
                    <p className="font-semibold mb-1">Pagamento</p>
                    <div className="flex justify-between"><span className="text-muted-foreground">Metodo</span><span>{(sel.payment || {}).method === "customer" ? "Cliente (Stripe)" : "Plafond dealer"}</span></div>
                    {sel.request_type === "attivazione" && (sel.payment || {}).charged && <div className="flex justify-between gap-3" data-testid="im-detail-activation-total"><span className="text-muted-foreground">Attivazione {euro((sel.payment || {}).activation_cost)} + canone {euro((sel.payment || {}).canone)} − agio {euro((sel.payment || {}).agio)}</span><span>{euro((sel.payment || {}).total)}{(sel.payment || {}).refunded ? " · rimborsato" : ""}</span></div>}
                    {sel.request_type === "attivazione" && "traffico_charged" in (sel.payment || {}) && <div className="flex justify-between"><span className="text-muted-foreground">Traffico addebitato</span><span>{(sel.payment || {}).traffico_charged ? euro((sel.payment || {}).min_topup) : "—"}</span></div>}
                    {sel.request_type === "cambio_promo" && <div className="flex justify-between" data-testid="im-detail-promo-total"><span className="text-muted-foreground">Cambio promo ({euro((sel.payment || {}).promo_fee)} + canone {euro((sel.payment || {}).canone)} − agio {euro((sel.payment || {}).agio)})</span><span>{euro((sel.payment || {}).total)}{(sel.payment || {}).refunded ? " · rimborsato" : ""}</span></div>}
                    {sel.request_type === "attivazione" && "activation_charged" in (sel.payment || {}) && <div className="flex justify-between"><span className="text-muted-foreground">Attivazione</span><span>{(sel.payment || {}).activation_charged ? euro((sel.payment || {}).activation_cost) : ((sel.payment || {}).method === "customer" ? "a carico cliente" : "—")}</span></div>}
                    {(sel.payment || {}).commission_credited && <div className="flex justify-between text-teal-600"><span>Provvigione accreditata</span><span>+{euro((sel.payment || {}).commission_amount ?? 15)}</span></div>}
                    {(sel.payment || {}).refunded && <div className="flex justify-between text-blue-500"><span>Storno effettuato</span><span>sì</span></div>}
                    {(sel.payment || {}).checkout_url && <a href={(sel.payment || {}).checkout_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">Link pagamento cliente (Stripe)</a>}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {sel.request_type === "attivazione" && <Button asChild size="sm" variant="outline" className="gap-1.5"><a href={pdaUrl(sel)} target="_blank" rel="noreferrer" data-testid="detail-pda"><FileText className="w-4 h-4" /> PDA</a></Button>}
                  {sel.has_doc && <Button asChild size="sm" variant="outline" className="gap-1.5"><a href={attUrl(sel, "doc")} target="_blank" rel="noreferrer" data-testid="detail-doc"><Download className="w-4 h-4" /> Documento</a></Button>}
                  {sel.has_bill && <Button asChild size="sm" variant="outline" className="gap-1.5"><a href={attUrl(sel, "bill")} target="_blank" rel="noreferrer" data-testid="detail-sim"><ImageIcon className="w-4 h-4" /> Foto SIM</a></Button>}
                  {sel.status === "completata" && ["attivazione", "cambio_promo", "ricarica"].includes(sel.request_type) && <PurchaseReceipt kind="im" refId={sel.id} />}
                </div>

                </div><div className="space-y-3">
                {sel.note_admin && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm" data-testid="detail-note-admin">
                    <p className="font-semibold text-amber-600 mb-1">Note dall'amministrazione</p>
                    <p className="whitespace-pre-wrap">{sel.note_admin}</p>
                  </div>
                )}

                <div data-testid="detail-history">
                  <p className="text-sm font-semibold flex items-center gap-1.5 mb-1.5"><Clock className="w-4 h-4 text-primary" /> Cronologia stati</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {(sel.status_history || []).slice().reverse().map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Badge className={(STATUS[h.status] || {}).cls || ""}>{(STATUS[h.status] || {}).label || h.status}</Badge>
                        <span className="text-muted-foreground text-xs">{fmt(h.at)}{h.by ? ` · ${h.by}` : ""}</span>
                      </div>
                    ))}
                    {(!sel.status_history || sel.status_history.length === 0) && <p className="text-xs text-muted-foreground">Nessuno storico disponibile.</p>}
                  </div>
                </div>
              </div></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
